import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  TextStyle,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Modal } from './Modal';
import { colors, spacing, textStyles } from '../theme';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useThemedStyles } from '../hooks/useThemedStyles';

export interface ForwardMessageSummary {
  _id?: string;
  messageId?: string;
  externalId?: string;
  type: string;
  content?: string;
  mediaUrl?: string;
  mediaType?: string;
  mediaName?: string;
  mediaSize?: number;
}

export interface ForwardTargetSelection {
  id: string;
  conversationId: string;
  contactId: string;
  contactName: string;
  phoneNumber: string;
  channel: string;
  providerId?: string;
  phoneNumberId?: string;
}

interface ForwardMessageModalProps {
  visible: boolean;
  sourceMessage: ForwardMessageSummary | null;
  currentConversationId?: string;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (targets: ForwardTargetSelection[]) => Promise<void> | void;
}

interface UnifiedConversation {
  id: string;
  conversationId: string;
  contactId: string;
  contactName: string;
  phoneNumber: string;
  channel: string;
  providerId?: string;
  phoneNumberId?: string;
  status?: string;
  lastMessageAt?: string;
  contactLastMessageAt?: string | null;
}

export const ForwardMessageModal: React.FC<ForwardMessageModalProps> = ({
  visible,
  sourceMessage,
  currentConversationId,
  isSubmitting = false,
  onClose,
  onConfirm,
}) => {
  const styles = useThemedStyles(getStyles);
  const { showNotification } = useAppContext();
  const isWeb = Platform.OS === 'web';
  const isTemplateSource = (sourceMessage?.type || '').toLowerCase() === 'template';
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState<UnifiedConversation[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [localSubmitting, setLocalSubmitting] = useState(false);

  const containerStyles = useMemo(
    () => [styles.container, isWeb ? styles.containerWeb : styles.containerMobile],
    [isWeb]
  );

  const submitting = isSubmitting || localSubmitting;

  useEffect(() => {
    if (visible) {
      fetchConversations('');
    } else {
      setSearchTerm('');
      setConversations([]);
      setSelectedIds(new Set());
      setLocalSubmitting(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const handler = setTimeout(() => {
      fetchConversations(searchTerm);
    }, 250);

    return () => clearTimeout(handler);
  }, [searchTerm, visible]);

  const fetchConversations = async (searchValue: string) => {
    try {
      setIsLoading(true);
      const response = await apiService.getUnifiedConversations({
        limit: 200,
        search: searchValue?.trim() || undefined,
        status: 'active',
        channel: 'whatsapp',
        offset: 0,
      });

      if (response.statusCode !== 1000 || !response.data) {
        showNotification(
          'error',
          'Forward Message',
          response.errors?.[0]?.message || 'Failed to load conversations'
        );
        return;
      }

      const raw = Array.isArray(response.data)
        ? (response.data as any[])
        : ((response.data.conversations || []) as any[]);

      const normalized: UnifiedConversation[] = raw
        .filter((item: any) => item?.channel?.toLowerCase?.() === 'whatsapp')
        .map(
          (item: any): UnifiedConversation => ({
            id: item.id || item.conversationId,
            conversationId: item.conversationId || item.id,
            contactId: item.contactId,
            contactName: item.contactName || item.phoneNumber || 'Unknown Contact',
            phoneNumber: item.phoneNumber,
            channel: item.channel,
            providerId: item.providerId,
            phoneNumberId: item.phoneNumberId,
            status: item.status,
            lastMessageAt: item.lastMessageAt,
            contactLastMessageAt: item.contactLastMessageAt ?? null,
          })
        )
        .filter((item) => item.id && item.id !== currentConversationId);

      setConversations(normalized);
    } catch (error: any) {
      console.error('Error loading conversations for forwarding:', error);
      showNotification(
        'error',
        'Forward Message',
        error?.message || 'Unexpected error while loading conversations'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isConversationWithinWindow = useCallback((conversation: UnifiedConversation) => {
    const status = (conversation.status || 'active').toLowerCase();
    if (status !== 'active') {
      return false;
    }

    const reference = conversation.contactLastMessageAt || conversation.lastMessageAt;
    if (!reference) {
      return false;
    }

    const parsed = new Date(reference);
    if (Number.isNaN(parsed.getTime())) {
      return false;
    }

    const hoursElapsed = (Date.now() - parsed.getTime()) / (1000 * 60 * 60);
    return hoursElapsed < 24;
  }, []);

  const selectableConversations = useMemo(
    () => conversations.filter(isConversationWithinWindow),
    [conversations, isConversationWithinWindow]
  );

  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set(selectableConversations.map((conversation) => conversation.id));
      const filtered = new Set(Array.from(prev).filter((id) => validIds.has(id)));
      return filtered.size === prev.size ? prev : filtered;
    });
  }, [selectableConversations]);

  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) {
      return selectableConversations;
    }

    const term = searchTerm.trim().toLowerCase();
    return selectableConversations.filter((conversation) => {
      const name = conversation.contactName?.toLowerCase?.() || '';
      const phone = conversation.phoneNumber?.toLowerCase?.() || '';
      return name.includes(term) || phone.includes(term);
    });
  }, [selectableConversations, searchTerm]);

  const selectedConversations = useMemo(
    () => selectableConversations.filter((conversation) => selectedIds.has(conversation.id)),
    [selectableConversations, selectedIds]
  );

  const toggleSelection = (conversationId: string) => {
    if (!selectableConversations.some((conversation) => conversation.id === conversationId)) {
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(conversationId)) {
        next.delete(conversationId);
      } else {
        next.add(conversationId);
      }
      return next;
    });
  };

  const formatFileSize = (size?: number) => {
    if (!size || size <= 0) {
      return null;
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    if (size < 1024 * 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const templatePreviewText = useMemo(() => {
    if (!isTemplateSource || !sourceMessage) {
      return undefined;
    }

    let payload: any = null;

    if (typeof sourceMessage.content === 'string') {
      try {
        payload = JSON.parse(sourceMessage.content);
      } catch (error) {
        payload = null;
      }
    } else if (sourceMessage.content && typeof sourceMessage.content === 'object') {
      payload = sourceMessage.content;
    }

    if (!payload && typeof sourceMessage.mediaType === 'string') {
      try {
        payload = JSON.parse(sourceMessage.mediaType);
      } catch (error) {
        payload = null;
      }
    }

    if (!payload) {
      return 'Template message';
    }

    const extractTemplateBody = (value: any): string | undefined => {
      if (!value) {
        return undefined;
      }

      if (typeof value?.body?.text === 'string') {
        return value.body.text;
      }

      if (typeof value?.body === 'string') {
        return value.body;
      }

      if (Array.isArray(value?.components)) {
        const bodyComponent = value.components.find(
          (component: any) => (component?.type || '').toUpperCase() === 'BODY'
        );

        if (bodyComponent) {
          if (typeof bodyComponent.text === 'string') {
            return bodyComponent.text;
          }

          if (Array.isArray(bodyComponent.parameters)) {
            return bodyComponent.parameters
              .map((param: any) => {
                if (typeof param?.text === 'string') {
                  return param.text;
                }
                if (typeof param?.title === 'string') {
                  return param.title;
                }
                if (typeof param?.payload === 'string') {
                  return param.payload;
                }
                if (typeof param === 'string') {
                  return param;
                }
                return '';
              })
              .filter(Boolean)
              .join(' ')
              .trim();
          }
        }
      }

      return undefined;
    };

    const body = extractTemplateBody(payload);
    if (body && body.length > 0) {
      return body.replace(/\*(.*?)\*/g, '$1');
    }

    return 'Template message';
  }, [isTemplateSource, sourceMessage]);

  const renderMessagePreview = () => {
    if (!sourceMessage) {
      return null;
    }

    const type = (sourceMessage.type || '').toLowerCase();
    const isTemplate = type === 'template';

    const typeLabel = (() => {
      switch (type) {
        case 'text':
          return 'Text';
        case 'image':
          return 'Image';
        case 'video':
          return 'Video';
        case 'audio':
          return 'Audio';
        case 'document':
          return 'Document';
        case 'location':
          return 'Location';
        case 'location_request':
          return 'Location Request';
        case 'contact':
          return 'Contact';
        case 'template':
          return 'Template';
        case 'interactive':
          return 'Interactive';
        case 'sticker':
          return 'Sticker';
        default:
          return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Message';
      }
    })();

    const typeIcon = (() => {
      switch (type) {
        case 'text':
          return 'chatbox-ellipses-outline';
        case 'image':
          return 'image-outline';
        case 'video':
          return 'videocam-outline';
        case 'audio':
          return 'musical-notes-outline';
        case 'document':
          return 'document-text-outline';
        case 'location':
        case 'location_request':
          return 'location-outline';
        case 'contact':
          return 'person-circle-outline';
        case 'template':
          return 'layers-outline';
        case 'interactive':
          return 'sparkles-outline';
        case 'sticker':
          return 'happy-outline';
        default:
          return 'chatbubble-outline';
      }
    })();

    const summary = isTemplate
      ? templatePreviewText || 'Template message'
      : sourceMessage.content?.trim?.() ||
        (typeof sourceMessage.content === 'string' ? sourceMessage.content : '') ||
        sourceMessage.mediaName ||
        sourceMessage.mediaType ||
        'No preview available';

    const metadata: Array<{ label: string; value: string }> = [];
    if (sourceMessage.mediaName) {
      metadata.push({ label: 'Name', value: sourceMessage.mediaName });
    }
    const sizeLabel = formatFileSize(sourceMessage.mediaSize);
    if (sizeLabel) {
      metadata.push({ label: 'Size', value: sizeLabel });
    }

    const renderMediaPreview = () => {
      if (!sourceMessage.mediaUrl) {
        return null;
      }

      if (type === 'image') {
        return (
          <Image
            source={{ uri: sourceMessage.mediaUrl }}
            style={styles.mediaThumbnail}
            resizeMode="cover"
          />
        );
      }

      let icon: keyof typeof Ionicons.glyphMap = 'document-outline';
      if (type === 'video') icon = 'videocam-outline';
      if (type === 'audio') icon = 'musical-notes-outline';

      return (
        <View style={styles.mediaPlaceholder}>
          <Ionicons name={icon} size={24} color={colors.primary} />
          <Text style={styles.mediaPlaceholderText} numberOfLines={1}>
            {sourceMessage.mediaName || 'Attachment'}
          </Text>
        </View>
      );
    };

    return (
      <View style={[styles.previewCard, isTemplate && styles.previewCardTemplate]}>
        <View style={styles.previewHeader}>
          <Ionicons name="arrow-redo-outline" size={18} color={colors.primary} />
          <Text style={styles.previewTitle}>Message to forward</Text>
        </View>
        <View style={styles.previewBody}>
          <Ionicons name={typeIcon} size={20} color={colors.primary} style={styles.previewIcon} />
          <View style={styles.previewTextContent}>
            <Text style={[styles.previewType, isTemplate && styles.previewTypeTemplate]}>
              {typeLabel}
            </Text>
            <Text style={[styles.previewSummary, isTemplate && styles.previewSummaryTemplate]}>
              {summary}
            </Text>
            {renderMediaPreview()}
            {metadata.length > 0 && (
              <View style={styles.previewMeta}>
                {metadata.map((item) => (
                  <View key={item.label} style={styles.previewMetaRow}>
                    <Text style={styles.previewMetaLabel}>{item.label}</Text>
                    <Text style={styles.previewMetaValue} numberOfLines={1}>
                      {item.value}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      </View>
    );
  };

  const handleSubmit = async () => {
    if (isTemplateSource) {
      showNotification('info', 'Forward Message', 'Template messages cannot be forwarded.');
      return;
    }

    if (selectedConversations.length === 0 || submitting) {
      return;
    }

    setLocalSubmitting(true);
    try {
      await onConfirm(
        selectedConversations.map((conversation) => ({
          id: conversation.id,
          conversationId: conversation.conversationId,
          contactId: conversation.contactId,
          contactName: conversation.contactName,
          phoneNumber: conversation.phoneNumber,
          channel: conversation.channel,
          providerId: conversation.providerId,
          phoneNumberId: conversation.phoneNumberId,
        }))
      );
      setSelectedIds(new Set());
    } finally {
      setLocalSubmitting(false);
    }
  };

  const renderConversationList = () => {
    if (isLoading) {
      return (
        <View style={styles.listLoading}>
          <ActivityIndicator color={colors.primary} />
          <Text style={styles.listLoadingText}>Loading conversations…</Text>
        </View>
      );
    }

    if (filteredConversations.length === 0) {
      return renderEmptyState();
    }

    return filteredConversations.map((conversation) => renderConversationCard(conversation));
  };

  const renderConversationCard = (conversation: UnifiedConversation) => {
    const isSelected = selectedIds.has(conversation.id);
    const disabled = isTemplateSource;

    return (
      <TouchableOpacity
        key={conversation.id}
        style={[
          styles.conversationRow,
          isSelected && styles.conversationRowSelected,
          disabled && styles.conversationRowDisabled,
        ]}
        activeOpacity={disabled ? 1 : 0.9}
        onPress={() => {
          if (!disabled) {
            toggleSelection(conversation.id);
          }
        }}
        disabled={disabled}
      >
        <View style={[styles.conversationAvatar, isSelected && styles.conversationAvatarSelected]}>
          <Text style={[styles.conversationAvatarText, isSelected && styles.conversationAvatarTextSelected]}>
            {conversation.contactName?.charAt(0)?.toUpperCase?.() || '#'}
          </Text>
        </View>

        <View style={styles.conversationContent}>
          <View style={styles.conversationNameRow}>
            <Text
              style={[styles.conversationName, disabled && styles.conversationNameDisabled]}
              numberOfLines={1}
            >
              {conversation.contactName}
            </Text>
            {!disabled && isSelected && (
              <Ionicons name="checkmark-circle" size={20} color={colors.success} />
            )}
          </View>

          <View style={styles.conversationMeta}>
            <Ionicons name="logo-whatsapp" size={12} color={colors.success} />
            <Text style={[styles.metaText, disabled && styles.metaTextDisabled]}>Active</Text>
            <Text style={[styles.metaSeparator, disabled && styles.metaTextDisabled]}>•</Text>
            <Text
              style={[styles.conversationPhone, disabled && styles.conversationPhoneDisabled]}
              numberOfLines={1}
            >
              {conversation.phoneNumber}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Ionicons name="chatbubble-ellipses-outline" size={48} color={colors.text.secondary} />
      <Text style={styles.emptyStateText}>No active WhatsApp conversations found</Text>
      <Text style={styles.emptyStateHint}>Try adjusting your search filters</Text>
    </View>
  );

  const footer = (
    <View style={styles.footer}>
      <TouchableOpacity
        style={[styles.footerButton, styles.cancelButton]}
        onPress={onClose}
        disabled={submitting}
      >
        <Text style={[styles.footerButtonText, styles.cancelButtonText]}>Cancel</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[
          styles.footerButton,
          styles.confirmButton,
          (selectedConversations.length === 0 || submitting) && styles.footerButtonDisabled,
        ]}
        onPress={handleSubmit}
        disabled={selectedConversations.length === 0 || submitting}
      >
        {submitting ? (
          <ActivityIndicator color={colors.white} />
        ) : (
          <Text style={[styles.footerButtonText, styles.confirmButtonText]}>
            Forward ({selectedConversations.length})
          </Text>
        )}
      </TouchableOpacity>
    </View>
  );

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      title="Forward Message"
      size={isWeb ? 'large' : 'full'}
      type="form"
      footer={footer}
      scrollable
      closeOnBackdrop={!submitting}
    >
      <View style={containerStyles}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.scrollContent,
            isWeb ? styles.scrollContentWeb : styles.scrollContentMobile,
          ]}
          keyboardShouldPersistTaps="handled"
        >
          {renderMessagePreview()}

          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Select Conversations</Text>
            <Text style={styles.sectionSubtitle}>
              Choose active WhatsApp conversations to forward this message to
            </Text>
          </View>

          <View style={styles.searchRow}>
            <Ionicons name="search" size={18} color={colors.text.secondary} />
            <TextInput
              style={styles.searchInput}
              value={searchTerm}
              placeholder="Search by name or phone"
              onChangeText={setSearchTerm}
              placeholderTextColor={colors.text.secondary}
              editable={!submitting}
              autoCorrect={false}
              autoCapitalize="none"
            />
          </View>

          <View style={styles.conversationList}>{renderConversationList()}</View>
        </ScrollView>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    width: '100%',
    paddingBottom: spacing.lg,
    flex: 1,
    alignSelf: 'stretch',
  },
  containerMobile: {
    paddingHorizontal: spacing.sm,
  },
  containerWeb: {
    paddingHorizontal: spacing.md,
    maxWidth: 820,
    width: '100%',
    alignSelf: 'center',
  },
  previewCard: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.lg,
    backgroundColor: colors.background.secondary,
  },
  previewCardTemplate: {
    backgroundColor: colors.background.primary,
    borderStyle: 'dashed',
  },
  previewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  previewTitle: {
    ...(textStyles.caption as TextStyle),
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  previewBody: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    width: '100%',
  },
  previewIcon: {
    marginTop: spacing.xs,
  },
  previewTextContent: {
    flex: 1,
    gap: spacing.xs,
    minWidth: 0,
  },
  previewType: {
    ...(textStyles.body as TextStyle),
    color: colors.text.primary,
    fontWeight: '600' as TextStyle['fontWeight'],
    fontSize: 16,
    lineHeight: 24,
  },
  previewTypeTemplate: {
    color: colors.text.secondary,
  },
  previewSummary: {
    ...(textStyles.body as TextStyle),
    color: colors.text.secondary,
    fontSize: 16,
    lineHeight: 24,
  },
  previewSummaryTemplate: {
    color: colors.text.secondary,
    opacity: 0.8,
  },
  previewMeta: {
    marginTop: spacing.sm,
    gap: spacing.xs,
  },
  previewMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
    width: '100%',
  },
  previewMetaLabel: {
    ...(textStyles.caption as TextStyle),
    color: colors.text.secondary,
    fontWeight: '600' as TextStyle['fontWeight'],
    fontSize: 13,
    lineHeight: 20,
  },
  previewMetaValue: {
    ...(textStyles.caption as TextStyle),
    color: colors.text.secondary,
    flexShrink: 1,
    minWidth: 0,
    fontSize: 13,
    lineHeight: 20,
  },
  mediaThumbnail: {
    marginTop: spacing.sm,
    width: 72,
    height: 72,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.background.primary,
  },
  mediaPlaceholder: {
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.primary,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  mediaPlaceholderText: {
    ...(textStyles.caption as TextStyle),
    color: colors.text.secondary,
    flex: 1,
    minWidth: 0,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.lg,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.primary,
  },
  searchInput: {
    flex: 1,
    ...(textStyles.body as TextStyle),
    color: colors.text.primary,
    paddingVertical: 0,
    fontSize: 15,
    lineHeight: 20,
  },
  conversationList: {
    width: '100%',
    marginTop: spacing.xs,
    paddingBottom: spacing.lg,
  },
  listLoading: {
    paddingVertical: spacing.xl,
    alignItems: 'center',
    gap: spacing.sm,
  },
  listLoadingText: {
    ...(textStyles.caption as TextStyle),
    color: colors.text.secondary,
  },
  conversationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.lg,
    backgroundColor: colors.background.primary,
    width: '100%',
  },
  conversationRowSelected: {
    backgroundColor: colors.background.secondary,
    borderColor: colors.primaryTransparent,
  },
  conversationAvatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: colors.primaryTransparent,
    justifyContent: 'center',
    alignItems: 'center',
  },
  conversationAvatarSelected: {
    backgroundColor: colors.primary,
  },
  conversationAvatarText: {
    ...(textStyles.caption as TextStyle),
    color: colors.primary,
    fontWeight: '600' as TextStyle['fontWeight'],
  },
  conversationAvatarTextSelected: {
    color: colors.white,
  },
  conversationContent: {
    flex: 1,
    minWidth: 0,
  },
  conversationNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    minWidth: 0,
  },
  conversationName: {
    ...(textStyles.body as TextStyle),
    color: colors.text.primary,
    flex: 1,
    fontWeight: '600' as TextStyle['fontWeight'],
    fontSize: 16,
    lineHeight: 22,
  },
  conversationMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
  },
  metaText: {
    ...(textStyles.caption as TextStyle),
    color: colors.success,
    fontWeight: '600' as TextStyle['fontWeight'],
    fontSize: 13,
    lineHeight: 18,
  },
  metaSeparator: {
    ...(textStyles.caption as TextStyle),
    color: colors.text.secondary,
    lineHeight: 18,
  },
  conversationPhone: {
    ...(textStyles.caption as TextStyle),
    color: colors.text.secondary,
    flexShrink: 1,
    minWidth: 0,
    fontSize: 13,
    lineHeight: 18,
  },
  conversationPhoneDisabled: {
    color: colors.text.secondary,
  },
  separator: {
    height: spacing.sm,
  },
  emptyState: {
    paddingVertical: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
  },
  emptyStateText: {
    ...(textStyles.body as TextStyle),
    color: colors.text.secondary,
    textAlign: 'center',
    fontSize: 16,
  },
  emptyStateHint: {
    ...(textStyles.caption as TextStyle),
    color: colors.text.secondary,
    textAlign: 'center',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: spacing.sm,
  },
  footerButton: {
    borderRadius: spacing.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    minWidth: 120,
    alignItems: 'center',
  },
  footerButtonText: {
    ...(textStyles.body as TextStyle),
    fontWeight: '600' as TextStyle['fontWeight'],
    fontSize: 16,
  },
  cancelButton: {
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cancelButtonText: {
    color: colors.text.primary,
  },
  confirmButton: {
    backgroundColor: colors.primary,
  },
  confirmButtonText: {
    color: colors.white,
  },
  footerButtonDisabled: {
    opacity: 0.5,
  },
  conversationRowDisabled: {
    opacity: 0.45,
  },
  conversationNameDisabled: {
    color: colors.text.secondary,
  },
  metaTextDisabled: {
    color: colors.text.secondary,
  },
  sectionHeader: {
    marginTop: spacing.lg,
    gap: spacing.xs,
  },
  sectionTitle: {
    ...(textStyles.body as TextStyle),
    fontSize: 18,
    fontWeight: '600' as TextStyle['fontWeight'],
    color: colors.text.primary,
  },
  sectionSubtitle: {
    ...(textStyles.caption as TextStyle),
    color: colors.text.secondary,
    fontSize: 14,
    lineHeight: 20,
    width: '100%',
  },
  scroll: {
    flex: 1,
    width: '100%',
  },
  scrollContent: {
    paddingBottom: spacing.lg,
  },
  scrollContentMobile: {
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.sm,
  },
  scrollContentWeb: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    maxWidth: 720,
    alignSelf: 'center',
  },
});

